import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/api';

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [businessEditing, setBusinessEditing] = useState(false);
  
  // Estados para datos personales
  const [personalData, setPersonalData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: ''
  });
  
  // Estados para datos empresariales
  const [businessData, setBusinessData] = useState({
    company_name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    location: '',
    website: ''
  });

  useEffect(() => {
    if (user) {
      setPersonalData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || ''
      });
      loadBusinessProfile();
    }
  }, [user]);

  const loadBusinessProfile = async () => {
    try {
      const response = await userService.getBusinessProfile();
      if (response.data) {
        setBusinessData({
          company_name: response.data.company_name || '',
          description: response.data.description || '',
          contact_email: response.data.contact_email || '',
          contact_phone: response.data.contact_phone || '',
          location: response.data.location || '',
          website: response.data.website || ''
        });
      }
    } catch (err) {
      console.log('No business profile found or error loading it');
    }
  };

  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPersonalData(prev => ({ ...prev, [name]: value }));
  };

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusinessData(prev => ({ ...prev, [name]: value }));
  };

  const handlePersonalSave = async () => {
    setLoading(true);
    setError('');
    try {
      await userService.updateProfile(personalData);
      setSuccess('Perfil personal actualizado correctamente');
      setEditing(false);
    } catch (err) {
      setError('Error al actualizar el perfil personal');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSave = async () => {
    setLoading(true);
    setError('');
    try {
      await userService.updateBusinessProfile(businessData);
      setSuccess('Perfil empresarial actualizado correctamente');
      setBusinessEditing(false);
    } catch (err) {
      setError('Error al actualizar el perfil empresarial');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><CircularProgress /></Box>;
  if (!user) return <Typography variant="h6">Debes iniciar sesión para ver tu perfil.</Typography>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>Mi Perfil</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <Grid container spacing={3}>
        {/* Información Personal */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Información Personal</Typography>
                <Box sx={{ ml: 'auto' }}>
                  {!editing ? (
                    <Tooltip title="Editar">
                      <IconButton onClick={() => setEditing(true)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box>
                      <Tooltip title="Guardar">
                        <IconButton onClick={handlePersonalSave} color="success" disabled={loading}>
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton onClick={() => setEditing(false)} color="error">
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Box display="flex" justifyContent="center" mb={3}>
                <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: 40 }}>
                  {user.username?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre de usuario"
                    name="username"
                    value={personalData.username}
                    onChange={handlePersonalChange}
                    disabled={!editing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={personalData.email}
                    onChange={handlePersonalChange}
                    disabled={!editing}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="first_name"
                    value={personalData.first_name}
                    onChange={handlePersonalChange}
                    disabled={!editing}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Apellido"
                    name="last_name"
                    value={personalData.last_name}
                    onChange={handlePersonalChange}
                    disabled={!editing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Chip 
                    label={`Rol: ${personalData.role === 'supplier' ? 'Proveedor' : 'Consumidor'}`}
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Información Empresarial */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6">Información Empresarial</Typography>
                <Box sx={{ ml: 'auto' }}>
                  {!businessEditing ? (
                    <Tooltip title="Editar">
                      <IconButton onClick={() => setBusinessEditing(true)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box>
                      <Tooltip title="Guardar">
                        <IconButton onClick={handleBusinessSave} color="success" disabled={loading}>
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton onClick={() => setBusinessEditing(false)} color="error">
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre de la empresa"
                    name="company_name"
                    value={businessData.company_name}
                    onChange={handleBusinessChange}
                    disabled={!businessEditing}
                    InputProps={{
                      startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descripción de la empresa"
                    name="description"
                    value={businessData.description}
                    onChange={handleBusinessChange}
                    disabled={!businessEditing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email de contacto"
                    name="contact_email"
                    type="email"
                    value={businessData.contact_email}
                    onChange={handleBusinessChange}
                    disabled={!businessEditing}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Teléfono de contacto"
                    name="contact_phone"
                    value={businessData.contact_phone}
                    onChange={handleBusinessChange}
                    disabled={!businessEditing}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ubicación"
                    name="location"
                    value={businessData.location}
                    onChange={handleBusinessChange}
                    disabled={!businessEditing}
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sitio web"
                    name="website"
                    value={businessData.website}
                    onChange={handleBusinessChange}
                    disabled={!businessEditing}
                    InputProps={{
                      startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile; 